import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * One-shot guard so a failed chunk load triggers at most a single hard reload
 * per browser session — never a reload loop.
 */
const RELOAD_FLAG = 'wk:chunk-reloaded';

/**
 * Recognise the family of errors browsers/Vite raise when a dynamically
 * imported module cannot be fetched or evaluated: stale chunk hashes after a
 * redeploy (prod) or a 504 "Outdated Optimize Dep" during dep re-optimization
 * (dev).
 */
function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|dynamically imported module|chunkloaderror/i.test(
    message,
  );
}

/**
 * `React.lazy` that survives transient dynamic-import failures.
 *
 * On the first failure that looks like a chunk-load error, it performs a single
 * hard reload (guarded by `sessionStorage`) so the browser pulls a fresh module
 * graph — the canonical recovery for both a Vite dev re-optimization and a
 * stale prod chunk. If the reload already happened (flag set) the error is
 * re-thrown so the route `errorElement` can render the branded fallback instead
 * of looping. On success the guard is cleared.
 *
 * The real root-cause fix for dev lives in `vite.config.ts` (`optimizeDeps`);
 * this wrapper is the runtime safety net that keeps the app self-healing.
 */
export function lazyWithRetry<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>,
): LazyExoticComponent<ComponentType<P>> {
  return lazy(async () => {
    try {
      const mod = await factory();
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* sessionStorage may be unavailable (private mode) — ignore. */
      }
      return mod;
    } catch (error) {
      if (isDynamicImportError(error)) {
        let alreadyReloaded = false;
        try {
          alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === '1';
          if (!alreadyReloaded) window.sessionStorage.setItem(RELOAD_FLAG, '1');
        } catch {
          /* ignore storage failures and fall through to the error boundary. */
        }
        if (!alreadyReloaded) {
          window.location.reload();
          // Park the promise; the reload supersedes this render entirely.
          return await new Promise<never>(() => {});
        }
      }
      throw error;
    }
  });
}
